import { useQuery } from '@tanstack/react-query'
import { ArrowLeft } from 'lucide-react'
import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { QueryState } from '@/components/QueryState'
import { Card, CardHeader } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import {
  ACCOUNT_TYPE_LABELS,
  NORMAL_BALANCE,
  formatMoney,
  formatSide,
  getGeneralLedger,
  startOfMonth,
  today,
} from '@/features/accounting/api'
import { listLocations } from '@/features/orders/api'

const SELECT_CLASS =
  'h-10 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 ' +
  'focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 ' +
  'dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'

/**
 * Every movement on one account, with the balance carried down the page.
 *
 * The opening balance is everything before the window rather than zero, which is what makes
 * the running column mean anything: a ledger that starts from nothing on the first of the
 * month is a list of transactions, not a ledger.
 */
export function GeneralLedger() {
  const { accountId = '' } = useParams()
  const [start, setStart] = useState(startOfMonth())
  const [end, setEnd] = useState(today())
  const [locationId, setLocationId] = useState('')

  const { data: locationsPage } = useQuery({
    queryKey: ['locations'],
    queryFn: () => listLocations(),
  })
  const locations = locationsPage?.data ?? []

  const { data, isLoading, error } = useQuery({
    queryKey: ['general-ledger', accountId, start, end, locationId],
    queryFn: () => getGeneralLedger(accountId, start, end, locationId || undefined),
    retry: false,
  })

  return (
    <div className="space-y-4">
      <Link
        to="/accounting/accounts"
        className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        Chart of accounts
      </Link>

      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">
            {data ? (
              <>
                <span className="font-mono text-base">{data.account.code}</span> {data.account.name}
              </>
            ) : (
              'General ledger'
            )}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {data
              ? `${ACCOUNT_TYPE_LABELS[data.account.type]} — increases on the ${
                  NORMAL_BALANCE[data.account.type]
                } side.`
              : 'Loading…'}
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-sm">
            <span className="block text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Location
            </span>
            <select
              aria-label="Location"
              className={`mt-1 ${SELECT_CLASS}`}
              value={locationId}
              onChange={(event) => setLocationId(event.target.value)}
            >
              <option value="">Any location</option>
              {locations.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            <span className="block text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
              From
            </span>
            <Input
              type="date"
              className="mt-1 w-auto"
              value={start}
              max={end}
              onChange={(event) => setStart(event.target.value)}
            />
          </label>
          <label className="text-sm">
            <span className="block text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
              To
            </span>
            <Input
              type="date"
              className="mt-1 w-auto"
              value={end}
              min={start}
              onChange={(event) => setEnd(event.target.value)}
            />
          </label>
        </div>
      </header>

      <Card>
        <CardHeader
          title="Movements"
          description="Oldest first, with the balance after each line. Opening carries in everything posted before the window."
        />
        <QueryState isLoading={isLoading} error={error}>
          {data && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <caption className="sr-only">
                  Ledger for {data.account.code} {data.account.name}
                </caption>
                <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:text-slate-400">
                  <tr>
                    <th className="px-5 py-2 font-medium">Date</th>
                    <th className="px-5 py-2 font-medium">Entry</th>
                    <th className="px-5 py-2 font-medium">Description</th>
                    <th className="px-5 py-2 text-right font-medium">Debit</th>
                    <th className="px-5 py-2 text-right font-medium">Credit</th>
                    <th className="px-5 py-2 text-right font-medium">Balance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  <tr className="text-slate-500 dark:text-slate-400">
                    <td className="px-5 py-2" colSpan={5}>
                      Opening balance
                    </td>
                    <td className="px-5 py-2 text-right tabular-nums">
                      {formatMoney(data.opening_balance)}
                    </td>
                  </tr>
                  {data.lines.map((line) => (
                    <tr key={`${line.entry_id}-${line.entry_number}-${line.balance}`}>
                      <td className="whitespace-nowrap px-5 py-2 text-slate-500 dark:text-slate-400">
                        {new Date(`${line.entry_date}T00:00:00`).toLocaleDateString()}
                      </td>
                      <td className="px-5 py-2 font-mono text-xs">
                        <Link
                          to={`/accounting/journal/${line.entry_id}`}
                          className="hover:underline"
                        >
                          {line.entry_number}
                        </Link>
                      </td>
                      <td className="px-5 py-2">{line.description}</td>
                      <td className="px-5 py-2 text-right tabular-nums">
                        {formatSide(line.debit)}
                      </td>
                      <td className="px-5 py-2 text-right tabular-nums">
                        {formatSide(line.credit)}
                      </td>
                      <td className="px-5 py-2 text-right tabular-nums">
                        {formatMoney(line.balance)}
                      </td>
                    </tr>
                  ))}
                  {data.lines.length === 0 && (
                    <tr>
                      <td className="px-5 py-6 text-slate-500 dark:text-slate-400" colSpan={6}>
                        Nothing posted to this account in the window.
                      </td>
                    </tr>
                  )}
                </tbody>
                <tfoot className="border-t border-slate-200 font-medium dark:border-slate-800">
                  <tr>
                    <td className="px-5 py-2" colSpan={5}>
                      Closing balance
                    </td>
                    <td className="px-5 py-2 text-right tabular-nums">
                      {formatMoney(data.closing_balance)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </QueryState>
      </Card>
    </div>
  )
}
